import React, {
  useCallback, useMemo,
} from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  TextField, InputAdornment, Icon,
} from '@picsio/ui';
import { InboxText } from '@picsio/icons';
import cn from 'classnames';

import { setUsageAltText } from '../../../store/products/actions';
import {
  resolveMediaAltDisplayState,
  resolveSharedMediaAltState,
  resolveUsageAltTextTarget,
  isPendingOwnedByCurrentContext,
} from '../../../store/helpers/mediaAlt';
import Logger from '../../../services/Logger';
import {
  useBlurCommit,
  useEnterToBlur,
  useLocalFieldValue,
} from '../../ProductInfo/hooks';
import Tooltip from '../../Tooltip';
import localization from '../../../shared/strings';
import productInfoStyles from '../../ProductInfo/styles.module.scss';
import {
  buildSharedVariantLabels,
  buildSharedAltAffectedUsages,
  resolvePendingOwnerLabel,
} from './helpers/mediaAltLabels';
import {
  confirmSharedAltClear,
  confirmSharedAltUpdate,
} from './helpers/confirmSharedAltDialogs';
import s from './MediaAltText.module.scss';

const ALT_MAX_LENGTH = 2048;

const trimAltTextValue = (value) => String(value ?? '').trim();

function MediaAltText({
  asset,
  productId,
  linkVariantId,
  isVariant,
  viewContext,
  disabled,
  productTitle,
  layout = 'row',
  chips = null,
}) {
  const dispatch = useDispatch();
  const products = useSelector((state) => state.products.items);
  const listingVariants = useSelector((state) => state.products?.entities?.listingVariants || {});
  const strings = localization.ALT_TEXT;

  const displayState = useMemo(() => resolveMediaAltDisplayState(asset, {
    viewContext,
    productId,
    variantId: linkVariantId,
    isVariant,
  }), [asset, viewContext, productId, linkVariantId, isVariant]);

  const sharedLabelContext = useMemo(() => ({
    products,
    listingId: displayState.listingId,
    asset,
    listingVariants,
  }), [asset, displayState.listingId, listingVariants, products]);

  const sharedState = useMemo(() => (
    displayState.listingId
      ? resolveSharedMediaAltState(asset, displayState.listingId)
      : null
  ), [asset, displayState.listingId]);

  const { localValue, setLocalValue } = useLocalFieldValue(displayState.value);
  const handleEnter = useEnterToBlur();

  const commitAltText = useCallback(async (nextValue) => {
    const target = resolveUsageAltTextTarget({
      viewContext,
      productId,
      linkVariantId,
      isVariant,
    });

    await dispatch(setUsageAltText({
      assetId: asset._id,
      altText: nextValue,
      ...target,
    })).unwrap();
  }, [asset._id, dispatch, isVariant, linkVariantId, productId, viewContext]);

  const saveValue = useCallback((nextValue) => {
    commitAltText(nextValue)
      .then(() => {
        Logger.log('User', 'ProductsMediaAltTextSaved', {
          assetId: asset._id,
          productId: productId || '',
          layer: viewContext?.layer || '',
          isClearing: nextValue === '',
          isShared: displayState.isShared,
        });
      })
      .catch(() => {});
  }, [
    asset._id,
    commitAltText,
    displayState.isShared,
    productId,
    viewContext?.layer,
  ]);

  const { handleBlur: commitOnBlur } = useBlurCommit({
    value: displayState.value,
    localValue,
    setLocalValue,
    onCommit: (nextValue) => {
      const trimmedNext = trimAltTextValue(nextValue);
      const trimmedCurrent = trimAltTextValue(displayState.value);

      if (trimmedNext === trimmedCurrent) return;

      const isClearing = trimmedNext === '';

      if (displayState.isShared) {
        const variantLabels = buildSharedVariantLabels({
          usedByVariants: sharedState?.usedByVariants,
          productTitle,
          ...sharedLabelContext,
        });
        const affectedUsages = buildSharedAltAffectedUsages({
          usedByProduct: sharedState?.usedByProduct,
          variantLabels,
        });
        const revertLocalValue = () => setLocalValue(displayState.value ?? '');

        if (isClearing) {
          confirmSharedAltClear({
            productTitle: productTitle || strings.productGallery,
            onConfirm: () => saveValue(trimmedNext),
            onCancel: revertLocalValue,
          });
          return;
        }

        confirmSharedAltUpdate({
          productTitle: productTitle || strings.productGallery,
          affectedUsages,
          onConfirm: () => saveValue(trimmedNext),
          onCancel: revertLocalValue,
        });
        return;
      }

      saveValue(trimmedNext);
    },
    serialize: trimAltTextValue,
  });

  const handleBlur = useCallback(() => {
    const trimmed = trimAltTextValue(localValue);
    if (trimmed !== localValue) {
      setLocalValue(trimmed);
    }
    commitOnBlur();
  }, [commitOnBlur, localValue, setLocalValue]);

  const pendingOwnerLabel = useMemo(() => resolvePendingOwnerLabel({
    pendingOwner: displayState.pendingOwner,
    products,
    listingId: displayState.listingId,
    listingVariants,
    productTitle,
    asset,
  }), [asset, displayState.listingId, displayState.pendingOwner, listingVariants, productTitle, products]);

  const isPendingOwnerContext = useMemo(() => isPendingOwnedByCurrentContext({
    pendingOwner: displayState.pendingOwner,
    viewContext,
    productId,
    linkVariantId,
    isVariant,
  }), [displayState.pendingOwner, viewContext, productId, linkVariantId, isVariant]);

  const showUnpublishedDot = displayState.hasUnpublishedChanges;
  const unpublishedDotTooltip = isPendingOwnerContext
    ? strings.unpublishedChangesTooltip
    : strings.pendingInDraft(pendingOwnerLabel || strings.productGallery);

  const isReadOnly = disabled || !displayState.isEditable;
  const placeholder = isReadOnly
    ? strings.placeholderReadOnlyEmpty
    : strings.placeholderEditable;
  const showSharedFieldIcon = layout === 'row' && displayState.isShared && displayState.isEditable;

  const sharedFieldAdornment = showSharedFieldIcon ? (
    <InputAdornment position="start" className={s.sharedFieldIcon}>
      <Tooltip content={strings.sharedFieldTooltip} placement="top">
        <span className={s.sharedFieldIconTrigger} aria-hidden>
          <Icon size="md">
            <InboxText />
          </Icon>
        </span>
      </Tooltip>
    </InputAdornment>
  ) : null;

  const field = (
    <TextField
      className={cn(s.field, { [s.fieldReadOnly]: isReadOnly, [s.fieldWithSharedIcon]: showSharedFieldIcon })}
      value={localValue}
      placeholder={placeholder}
      disabled={isReadOnly}
      InputProps={{
        startAdornment: sharedFieldAdornment,
        maxLength: ALT_MAX_LENGTH,
        onKeyDown: handleEnter,
      }}
      onChange={(event) => {
        if (isReadOnly) return;
        setLocalValue(event.target.value);
      }}
      onBlur={handleBlur}
    />
  );

  const fieldTooltipContent = isReadOnly
    ? strings.publishedAltReadOnlyTooltip
    : null;

  const fieldWithTooltip = (
    <Tooltip content={fieldTooltipContent} placement="top">
      <span className={s.fieldTooltip}>
        {field}
      </span>
    </Tooltip>
  );

  return (
    <div className={cn(s.root, layout === 'stack' && s.rootStack, { disabled: isReadOnly })}>
      <If condition={layout === 'stack' && chips}>
        <div className={s.chips}>{chips}</div>
      </If>
      <div className={cn(s.fieldColumn, layout === 'stack' && s.fieldColumnStack)}>
        <div className={s.fieldRow}>
          {fieldWithTooltip}
          <If condition={showUnpublishedDot}>
            <Tooltip content={unpublishedDotTooltip} placement="top">
              <span className={cn(productInfoStyles.diffDot, s.unpublishedDot)} />
            </Tooltip>
          </If>
        </div>
      </div>
    </div>
  );
}

MediaAltText.propTypes = {
  asset: PropTypes.shape({
    _id: PropTypes.string.isRequired,
  }).isRequired,
  productId: PropTypes.string,
  linkVariantId: PropTypes.string,
  isVariant: PropTypes.bool,
  viewContext: PropTypes.shape({
    layer: PropTypes.string,
    listingProductId: PropTypes.string,
    masterProductId: PropTypes.string,
    channelId: PropTypes.string,
  }),
  disabled: PropTypes.bool,
  productTitle: PropTypes.string,
  layout: PropTypes.oneOf(['row', 'stack']),
  chips: PropTypes.node,
};

MediaAltText.defaultProps = {
  productId: null,
  linkVariantId: null,
  isVariant: false,
  viewContext: null,
  disabled: false,
  productTitle: '',
  layout: 'row',
  chips: null,
};

export default MediaAltText;
